/**
 * RÉFÉRENTIELS → MOTIFS — ce qui EXPLIQUE un geste : une remise, un crédit de cagnotte, un écart (la maquette et son brief ;
 * écarts décidés : `CONVENTIONS-PRODUCTION.md` §1quater).
 *
 * ⚠️ UN ONGLET PAR PORTÉE (`JUSTIFICATION_SCOPES`, plus les motifs sans portée : écarts de stock et de bons) et la liste
 *    FIXE des corrections de stock, en lecture seule. Chaque liste est celle que son geste propose, dans cet ordre.
 * ⚠️ UN MOTIF QUI A SERVI NE SE SUPPRIME PLUS, IL SE MASQUE : il n'est plus proposé et l'historique garde son libellé (la
 *    doctrine des moyens de règlement). La corbeille n'existe que pour un motif jamais employé (`usages`, le compte de la
 *    garde du producteur).
 * ⚠️ LE DÉFAUT (remises, crédit de cagnotte) est celui du contrat (`effectiveDefaultReason`) — la règle que la caisse
 *    appliquera — : il ne se masque ni ne se supprime. Un écart n'en a pas : il se justifie par un choix explicite.
 * ⚠️ LA CAISSE N'APPLIQUE LE DÉFAUT CHOISI ET LES MOTIFS MASQUÉS QU'AVEC SA PROCHAINE VERSION, et la note de ces portées le
 *    dit ; au siège (Z & clôtures, centre d'écarts), un motif masqué n'est déjà plus proposé.
 * ⚠️ RENOMMER EST SÛR : le schéma pointe le référentiel au lieu d'en recopier le mot, l'historique suit.
 */
import { useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirm, Icon, rovingRadio, showToast, Tabs, Tooltip, useTileDrag, type IconName } from '@orgazone/ui-web';
import { effectiveDefaultReason, isDefaultableScope, normalizeCode, proposeReasonCode } from '@orgazone/sync-contract';
import { api, type ReferentialJustification, type RegisterReasonsPending } from '../api';
import { key, t, type Translatable } from '../i18n';
import { rankWrites, reasonOf } from './referential-facets-shared';
import { MOTIFS_CORRECTION } from './receptions-shared';

type CategoryKey = 'rem' | 'caisse' | 'cag' | 'gen';
type TabKey = CategoryKey | 'fix';

/** Une portée : où ses motifs sont proposés, ce que son absence coûte, et comment son usage se dit. */
interface Category {
  k: CategoryKey;
  scope: string | null;
  label: Translatable;
  where: Translatable;
  empty: Translatable;
  icon: IconName;
}

/** Les portées, dans l'ordre du dessin. ⚠️ Seules celles-ci sont LUES par un geste : une autre ferait des motifs invisibles. */
const CATEGORIES: readonly Category[] = [
  { k: 'rem', scope: 'discount', label: key('Remises'), icon: 'tag',
    where: key('Proposés dans la fenêtre de remise de la caisse, sur une ligne ou sur tout le ticket.'),
    empty: key('Sans motif, les remises de la caisse s’enregistrent sans motif.') },
  { k: 'caisse', scope: 'cash_variance', label: key('Écarts de caisse'), icon: 'cash',
    where: key('Proposés pour justifier un écart d’espèces, dans Z & clôtures.'),
    empty: key('Sans motif, un écart d’espèces ne pourra pas être justifié.') },
  { k: 'cag', scope: 'loyalty_credit', label: key('Crédit de cagnotte'), icon: 'star',
    where: key('Proposés quand on crédite à la main la cagnotte d’un client.'),
    empty: key('Sans motif, un crédit manuel de cagnotte s’enregistre sans motif.') },
  { k: 'gen', scope: null, label: key('Écarts de stock et de bons'), icon: 'box',
    where: key('Proposés pour expliquer un écart de stock ou de bon de livraison, dans le centre d’écarts.'),
    empty: key('Sans motif, un écart de stock ou de bon ne pourra pas être expliqué.') },
];

/** Quand utiliser chaque correction de stock — la liste fixe du système, que le grand livre compte par cause. */
const FIXED_WHEN: Record<string, Translatable> = {
  inventory: key('Écart constaté au comptage d’inventaire.'),
  breakage: key('Pièce abîmée, sortie du stock.'),
  theft: key('Démarque constatée — pièce disparue.'),
  manual_correction: key('Erreur de saisie corrigée à la main.'),
};

/** La portée d'un motif telle que l'écran la range : vide = sans portée. */
const scopeOf = (m: ReferentialJustification): string | null => (m.scope == null || m.scope === '' ? null : m.scope);

/**
 * CE QUE LE GESTE A FAIT AVEC CE MOTIF, au mot du geste — « 34 lignes · 3 tickets », « 23 écarts justifiés ».
 * ⚠️ Un motif cité ailleurs que par son geste (une vente annulée, une autre portée) n'est pas « jamais utilisé » : son usage
 *    total se dit, puisque c'est lui qui retient la corbeille.
 */
export function usedFor(m: ReferentialJustification): string {
  const u = m.uses;
  const scope = scopeOf(m);
  const own = scope === 'discount' ? [u.lines ? t('{n} ligne', { n: u.lines }) : null, u.tickets ? t('{n} ticket', { n: u.tickets }) : null].filter((x): x is string => !!x).join(' · ')
    : scope === 'cash_variance' ? (u.cashNotes ? t('{n} écart justifié', { n: u.cashNotes }) : '')
    : scope === 'loyalty_credit' ? (u.credits ? t('{n} crédit', { n: u.credits }) : '')
    : (u.stockNotes ? t('{n} écart expliqué', { n: u.stockNotes }) : '');
  if (own) return own;
  return m.usages > 0 ? t('Cité {n} fois', { n: m.usages }) : t('Jamais utilisé');
}

const HEAD: CSSProperties = {
  display: 'grid', gap: 12, alignItems: 'center', padding: '9px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600,
};
const ICON_BUTTON: CSSProperties = {
  width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer',
};

export function OngletMotifs({ jeu, registerReasons, failure, reload }: {
  jeu: ReferentialJustification[] | null;
  /** Ce que la reprise de l'ancienne liste de la caisse ajouterait — `null` tant que rien n'est lu. */
  registerReasons: RegisterReasonsPending | null;
  failure: string | null; reload: () => void;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('rem');
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCode, setNewCode] = useState('');
  const [busy, setBusy] = useState(false);

  const category = CATEGORIES.find((c) => c.k === tab) ?? null;
  const all = jeu ?? [];
  const rows = category ? all.filter((m) => scopeOf(m) === category.scope) : [];
  const defaultable = !!category && isDefaultableScope(category.scope);
  const effective = category ? effectiveDefaultReason(rows, category.scope) : null;

  /** Une écriture du référentiel, son message de réussite, et la relecture — l'échec se dit avec son motif réel. */
  const write = async (gesture: () => Promise<unknown>, done: string): Promise<boolean> => {
    setBusy(true);
    try {
      await gesture();
      showToast(done);
      reload();
      return true;
    } catch (e) {
      showToast(reasonOf(e), 'err');
      return false;
    } finally { setBusy(false); }
  };

  const drag = useTileDrag({
    keys: rows.map((m) => m.code),
    axis: 'y',
    disabled: busy || !category,
    ignoreSelector: '[data-reason-action]',
    // ⚠️ L'ORDRE EST UNE DONNÉE ÉCRITE : la portée est renumérotée par le producteur, puis relue — jamais un ordre que le
    //    serveur n'a peut-être pas accepté.
    onReorder: (next) => {
      const writes = rankWrites(rows, next);
      if (!writes.length) return;
      void write(() => api.writeMasterData('justification', writes, `motif:order:${category?.k}:${next.join(',')}`),
        t('Ordre enregistré.'));
    },
  });

  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {t('Le référentiel n’a pas pu être lu : {failure}. Ce n’est pas « aucun motif ».', { failure })}</p>
    );
  }
  if (!jeu) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du référentiel…')}</p>;

  const label = newLabel.trim();
  const code = newCode.trim() || proposeReasonCode(label, all.map((m) => m.code));
  const addError = !category || !label ? ''
    : rows.some((m) => normalizeCode(m.label) === normalizeCode(label)) ? t('Ce motif existe déjà dans « {v} ».', { v: t(category.label) })
    : newCode.trim() && all.some((m) => normalizeCode(m.code) === normalizeCode(code)) ? t('Ce code est déjà pris par un autre motif.')
    : '';
  const canAdd = !!category && !!label && !addError && !busy;

  const closeAdd = () => { setAdding(false); setNewLabel(''); setNewCode(''); };
  const add = async () => {
    if (!canAdd || !category) return;
    const sortOrder = all.reduce((max, m) => Math.max(max, m.sortOrder), 0) + 1;
    const ok = await write(() => api.createMasterDataOnly('justification', { code, label, scope: category.scope, sortOrder }, `motif:create:${code}`),
      t('« {v} » ajouté — il descendra aux caisses à la prochaine synchronisation.', { v: label }));
    if (ok) closeAdd();
  };

  const makeDefault = (m: ReferentialJustification) => {
    if (!m.isActive) { showToast(t('Réaffichez « {v} » avant d’en faire le motif par défaut.', { v: m.label }), 'err'); return; }
    // ⚠️ L'ANCIENNE MARQUE PART DANS LE MÊME LOT, AVANT la nouvelle : le producteur n'accepte qu'un défaut par portée.
    const entries = [
      ...rows.filter((r) => r.isDefault && r.code !== m.code).map((r) => ({ code: r.code, isDefault: false })),
      { code: m.code, isDefault: true },
    ];
    void write(() => api.writeMasterData('justification', entries, `motif:default:${m.code}`),
      t('« {v} » est désormais le motif proposé par défaut.', { v: m.label }));
  };

  const toggleHidden = (m: ReferentialJustification) => {
    void write(() => api.writeMasterData('justification', [{ code: m.code, isActive: !m.isActive }], `motif:${m.isActive ? 'hide' : 'show'}:${m.code}`),
      m.isActive ? t('« {v} » masqué — il n’est plus proposé ; l’historique garde son libellé.', { v: m.label })
        : t('« {v} » réaffiché — de nouveau proposé.', { v: m.label }));
  };

  const remove = async (m: ReferentialJustification) => {
    const ok = await confirm({
      title: t('Supprimer « {v} » ?', { v: m.label }), message: t('Il n’a encore jamais été utilisé : rien ne sera perdu.'),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    void write(() => api.deleteReferentielEntry('justification', { code: m.code }, `motif:delete:${m.code}`), t('« {v} » supprimé.', { v: m.label }));
  };

  /** Renomme sur place — rend `true` quand le libellé est écrit ; un libellé vidé reprend l'ancien. */
  const rename = (m: ReferentialJustification, value: string): Promise<boolean> => {
    const v = value.replace(/\s+/g, ' ').trim();
    if (!v || v === m.label) return Promise.resolve(false);
    return write(() => api.writeMasterData('justification', [{ code: m.code, label: v }], `motif:rename:${m.code}:${v}`),
      t('« {v} » renommé — l’historique suit.', { v }));
  };

  const reprendre = () => {
    void (async () => {
      setBusy(true);
      try {
        const r = await api.importRegisterReasons('justification:import-register-reasons');
        showToast(r.created > 0 ? t('{n} motif repris dans le référentiel.', { n: r.created }) : t('Rien à reprendre : ces motifs sont déjà au référentiel.'));
        reload();
      } catch (e) {
        showToast(reasonOf(e), 'err');
      } finally { setBusy(false); }
    })();
  };

  const columns = defaultable ? '26px 52px minmax(170px,1fr) minmax(0,150px) minmax(0,140px) 34px' : '26px minmax(170px,1fr) minmax(0,150px) minmax(0,140px) 34px';
  const byCode = new Map(rows.map((m) => [m.code, m]));
  const tabs = [
    ...CATEGORIES.map((c) => {
      const n = all.filter((m) => scopeOf(m) === c.scope).length;
      return { id: c.k as TabKey, label: t(c.label), icon: c.icon, count: n, countTone: n ? 'neutral' as const : 'warn' as const };
    }),
    { id: 'fix' as TabKey, label: t('Corrections de stock'), icon: 'lock' as IconName, count: MOTIFS_CORRECTION.length, countTone: 'neutral' as const },
  ];

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ink-900)' }}>{t('Motifs')}</h2>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--ink-600)', lineHeight: 1.5, maxWidth: 760, textWrap: 'pretty' }}>
          {t('Les raisons qu’un opérateur choisit pour justifier un geste — une remise, un crédit de cagnotte, un écart. Chaque liste est celle que le geste correspondant propose, dans cet ordre.')}
        </p>
      </div>
      <Tabs items={tabs} value={tab} onChange={(k) => { setTab(k); closeAdd(); }} panelId="bo-motifs-panel" label={t('Portées des motifs')} size="strip" showZero />
      <div id="bo-motifs-panel" role="tabpanel" aria-labelledby={`bo-motifs-panel-tab-${tab}`} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 16px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: '1 1 280px', minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{category ? t(category.label) : t('Corrections de stock')}</span>
            <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.45 }}>
              {category ? t(category.where) : t('Proposées à chaque ajustement manuel du stock.')}</span>
          </span>
          {category ? (
            <button type="button" onClick={() => setAdding(true)} className="oz-motif-new"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <Icon name="plus" size={13} stroke={2.4} />{t('Nouveau motif')}
            </button>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 11px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <Icon name="lock" size={12} stroke={2.2} />{t('Liste fixe du système')}
            </span>
          )}
        </div>

        {category?.k === 'rem' && registerReasons && registerReasons.pending.length > 0 ? (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px', background: 'var(--teal-50)', borderBottom: '1px solid var(--ink-100)' }}>
            <Icon name="history" size={15} stroke={2} color="var(--teal-800)" />
            <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.45 }}>
              {t('La caisse proposait {n} motif de remise absent d’ici : {v}.', { n: registerReasons.pending.length, v: registerReasons.pending.join(', ') })}
            </span>
            <button type="button" disabled={busy} onClick={reprendre}
              style={{ height: 30, padding: '0 12px', border: '1px solid var(--teal-800)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              {t('Reprendre ces motifs')}</button>
          </div>
        ) : null}
        {category?.k === 'rem' && registerReasons && registerReasons.unreadable.length > 0 ? (
          <div role="status" style={{ padding: '10px 16px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.45 }}>
            {t('La liste de motifs de la caisse n’a pas pu être lue ({v}) : rien n’en sera repris.', { v: registerReasons.unreadable.join(', ') })}
          </div>
        ) : null}

        {category ? (
          <>
            <div style={{ ...HEAD, gridTemplateColumns: columns }}>
              <span />
              {defaultable ? <Tooltip content={t('Le motif présélectionné quand la fenêtre s’ouvre en caisse')}><span style={{ whiteSpace: 'nowrap' }}>{t('Défaut')}</span></Tooltip> : null}
              <span>{t('Libellé')}</span><span>{t('Code')}</span><span>{t('Utilisé pour')}</span><span />
            </div>
            {adding ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 16px', background: 'var(--teal-50)', borderBottom: '1px solid var(--ink-200)' }}>
                <input autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder={t('Libellé — ex. Erreur de prix')} aria-label={t('Libellé du nouveau motif')}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } else if (e.key === 'Escape') closeAdd(); }}
                  className="oz-ref-input"
                  style={{ flex: '1 1 220px', minWidth: 0, fontSize: '13px', height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
                <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 24))}
                  placeholder={label ? code : t('Proposé depuis le libellé')} aria-label={t('Code du nouveau motif')} title={t('Proposé depuis le libellé — modifiable')}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } else if (e.key === 'Escape') closeAdd(); }}
                  className="oz-ref-input"
                  style={{ flex: '0 1 200px', minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: '11.5px', textTransform: 'uppercase', height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
                <button type="button" disabled={!canAdd} onClick={() => void add()}
                  style={{ height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: canAdd ? 'var(--teal-500)' : 'var(--ink-200)', color: canAdd ? '#fff' : 'var(--ink-400)', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: canAdd ? 'pointer' : 'default' }}>
                  {t('Ajouter')}</button>
                <button type="button" onClick={closeAdd}
                  style={{ height: 34, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {t('Annuler')}</button>
                {addError ? <span role="alert" style={{ flexBasis: '100%', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--rose-fg)' }}>{addError}</span> : null}
              </div>
            ) : null}
            {/* ⚠️ UN GROUPE RADIO PAR PORTÉE : la tabulation entre sur le défaut, les flèches en choisissent un autre. */}
            <div role={defaultable ? 'radiogroup' : undefined} aria-label={defaultable ? t('Motif proposé par défaut dans la fenêtre de la caisse') : undefined}>
            {drag.order.map((c, j) => {
              const m = byCode.get(c);
              if (!m) return null;
              if (drag.grabbed === c) return <div key={c}>{drag.placeholder}</div>;
              const isDefault = effective?.code === m.code;
              const linked = category.scope === 'discount' && (m.uses.lines > 0 || m.uses.tickets > 0);
              return (
                <div key={c} {...drag.tile(c)}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: columns, gap: 12, alignItems: 'center', minHeight: 50, padding: '0 16px',
                    borderBottom: j < rows.length - 1 ? '1px solid var(--ink-100)' : 'none', background: m.isActive ? 'var(--paper)' : 'var(--ink-50)',
                  }}>
                    <Tooltip content={t('Glisser pour changer l’ordre proposé')}>
                      <span aria-label={t('Changer l’ordre de « {v} »', { v: m.label })} {...drag.handle(c)}
                        style={{ ...drag.handle(c).style, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 32, color: 'var(--ink-300)', cursor: 'grab', touchAction: 'none' }}>
                        <Icon name="drag" size={16} />
                      </span>
                    </Tooltip>
                    {defaultable ? (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {/* ⚠️ PAS `disabled` PENDANT L'ÉCRITURE : un bouton désactivé perd le focus que les flèches viennent d'y poser ;
                            il se déclare inactif et refuse le geste. */}
                        <button type="button" role="radio" aria-checked={isDefault} aria-label={t('Motif par défaut : « {v} »', { v: m.label })} data-reason-action
                          title={isDefault ? t('Motif proposé par défaut dans la fenêtre de la caisse') : t('Proposer ce motif par défaut')}
                          aria-disabled={busy || undefined} onClick={() => { if (!busy && !isDefault) makeDefault(m); }}
                          {...rovingRadio({
                            index: j, count: drag.order.length, checked: isDefault, anyChecked: effective !== null,
                            onPick: (i) => { const target = byCode.get(drag.order[i] ?? ''); if (!busy && target) makeDefault(target); },
                            disabled: (i) => !byCode.get(drag.order[i] ?? '')?.isActive,
                          })}
                          style={{ width: 44, height: 44, marginLeft: -12, border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isDefault ? 'default' : 'pointer' }}>
                          <span style={{ width: 20, height: 20, border: `2px solid ${isDefault ? 'var(--teal-500)' : 'var(--ink-300)'}`, borderRadius: 'var(--r-pill)', background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                            <span style={{ width: 10, height: 10, borderRadius: 'var(--r-pill)', background: isDefault ? 'var(--teal-500)' : 'transparent' }} />
                          </span>
                        </button>
                      </span>
                    ) : null}
                    <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <RenameInput value={m.label} muted={!m.isActive} disabled={busy} onRename={(v) => rename(m, v)} />
                      {!m.isActive ? (
                        <Tooltip content={t('Plus proposé — l’historique garde son libellé')}>
                          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 600 }}>{t('Masqué')}</span>
                        </Tooltip>
                      ) : null}
                    </span>
                    <span title={m.code} style={{ minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.code}</span>
                    <span style={{ minWidth: 0, display: 'flex' }}>
                      {linked ? (
                        <button type="button" data-reason-action title={t('Voir ces remises dans Statistiques → Ventes → Remises')}
                          onClick={() => navigate(`/stats/ventes?onglet=remises&motif=${encodeURIComponent(m.code)}`)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%', height: 28, padding: '0 8px', marginLeft: -8, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', cursor: 'pointer' }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{usedFor(m)}</span><Icon name="arrowR" size={12} stroke={2.2} />
                        </button>
                      ) : (
                        <span style={{ fontSize: 'var(--t-body)', color: m.usages > 0 ? 'var(--ink-800)' : 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>{usedFor(m)}</span>
                      )}
                    </span>
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isDefault ? (
                        <Tooltip content={t('Motif par défaut : choisissez-en un autre avant de le masquer')}>
                          <span style={{ ...ICON_BUTTON, cursor: 'default', color: 'var(--ink-300)' }}><Icon name="lock" size={14} stroke={2} /></span>
                        </Tooltip>
                      ) : m.usages > 0 ? (
                        <button type="button" data-reason-action disabled={busy} onClick={() => toggleHidden(m)}
                          aria-label={m.isActive ? t('Masquer « {v} »', { v: m.label }) : t('Réafficher « {v} »', { v: m.label })}
                          title={m.isActive ? t('Masquer — il n’est plus proposé ; l’historique le garde') : t('Réafficher — de nouveau proposé')}
                          style={{ ...ICON_BUTTON, color: m.isActive ? 'var(--ink-500)' : 'var(--teal-800)' }}>
                          <Icon name={m.isActive ? 'eyeOff' : 'eye'} size={16} stroke={1.9} />
                        </button>
                      ) : (
                        <button type="button" data-reason-action disabled={busy} onClick={() => void remove(m)}
                          aria-label={t('Supprimer « {v} »', { v: m.label })} title={t('Jamais utilisé : il se supprime sans rien perdre')}
                          style={{ ...ICON_BUTTON, color: 'var(--rose-fg)' }}>
                          <Icon name="trash" size={15} stroke={2} />
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
            </div>
            {rows.length === 0 && !adding ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, margin: 16, padding: '26px 16px', border: '1.5px dashed var(--ink-200)', borderRadius: 'var(--r-sm)', textAlign: 'center' }}>
                <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-800)' }}>{t('Aucun motif')}</span>
                <span style={{ fontSize: 'var(--t-body)', color: 'var(--amber-fg)', fontWeight: 600, lineHeight: 1.45 }}>{t(category.empty)}</span>
                <button type="button" onClick={() => setAdding(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer' }}>
                  <Icon name="plus" size={13} stroke={2.2} />{t('Nouveau motif')}</button>
              </div>
            ) : null}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '11px 16px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
              <Icon name="info" size={14} stroke={2} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {defaultable
                  ? t('Renommer un motif est sans risque, l’historique suit. Un motif déjà utilisé ne se supprime pas : masquez-le — il n’est plus proposé et l’historique garde son libellé. La caisse appliquera le motif par défaut choisi et les motifs masqués avec sa prochaine version.')
                  : t('Aucun motif n’est présélectionné ici : un écart se justifie toujours par un choix explicite. Renommer un motif est sans risque, l’historique suit. Un motif déjà utilisé ne se supprime pas : masquez-le — il n’est plus proposé et l’historique garde son libellé.')}
              </span>
            </div>
          </>
        ) : (
          <>
            <div style={{ ...HEAD, gridTemplateColumns: 'minmax(0,220px) 190px minmax(0,1fr)' }}>
              <span>{t('Libellé')}</span><span>{t('Code')}</span><span>{t('Quand l’utiliser')}</span>
            </div>
            {MOTIFS_CORRECTION.map((f) => (
              <div key={f.v} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,220px) 190px minmax(0,1fr)', gap: 12, alignItems: 'center', minHeight: 46, padding: '0 16px', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>
                  <Icon name="lock" size={12} stroke={2.2} color="var(--ink-400)" />{t(f.l)}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-500)' }}>{f.v}</span>
                <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{FIXED_WHEN[f.v] ? t(FIXED_WHEN[f.v]!) : ''}</span>
              </div>
            ))}
            <div style={{ padding: '11px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
              {t('Fixée par le système : c’est elle qui permet de compter les écarts de stock par cause (inventaire, casse, vol). Elle ne se modifie pas.')}
            </div>
          </>
        )}
      </div>
      {drag.ghost}
    </div>
  );
}

/**
 * LE LIBELLÉ, RENOMMABLE SUR PLACE — il s'écrit en quittant la case, Entrée valide, Échap reprend l'ancien.
 * ⚠️ UN LIBELLÉ VIDÉ REPREND L'ANCIEN : un motif sans nom n'expliquerait rien. Un refus du serveur le reprend aussi.
 */
function RenameInput({ value, muted, disabled, onRename }: {
  value: string; muted: boolean; disabled: boolean; onRename: (v: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value;
  const commit = async () => {
    if (draft === null) return;
    await onRename(draft);
    // Écrit, la relecture apporte le nouveau libellé ; refusé ou vidé, la case reprend celui qui est enregistré.
    setDraft(null);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    else if (e.key === 'Escape') { setDraft(null); }
  };
  return (
    <input value={shown} disabled={disabled} spellCheck={false} aria-label={t('Libellé du motif « {v} »', { v: value })} title={t('Cliquer pour renommer — l’historique suit')}
      onChange={(e) => setDraft(e.target.value)} onBlur={() => void commit()} onKeyDown={onKey} data-reason-action className="oz-motif-label"
      style={{ flex: 1, minWidth: 0, height: 32, padding: '0 8px', marginLeft: -9, border: '1px solid transparent', borderRadius: 'var(--r-xs)', background: 'transparent', fontSize: 'var(--t-title)', fontWeight: 600, color: muted ? 'var(--ink-500)' : 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
  );
}
